/**
 * Per-class denomination + owned-label resolver.
 *
 * Layered on top of the existing client-wide `clientData.use_fund_language`
 * flag. The resolver lets a single class override the client-wide
 * denomination (e.g., ZEPH on a fund-language client) and lets admins
 * configure the displayed label per class or per client.
 *
 * Resolution priority for `getEffectiveDenomination`:
 *   1. `clientData.equity[classKey].denomination`            (per-class override)
 *   2. `'dollars'` when `clientData.use_fund_language === true`
 *   3. `'shares'`
 *
 * Resolution priority for `resolveOwnedLabel`:
 *   1. `clientData.equity[classKey].label_owned`             — only consulted when
 *      the same class has an explicit `denomination` override. The class-level
 *      label is intentionally coupled with the denomination override so an
 *      admin setting a per-class denomination also names the matching label.
 *   2. `clientData.language.label_owned`                     — client-wide override
 *   3. `'Contributed'` when `clientData.use_fund_language === true`
 *   4. `null` (the caller's existing platform default applies)
 *
 * Compatibility contract: this resolver is **additive**. It only returns a
 * non-null label when one of the new keys (`equity[classKey].label_owned` or
 * `language.label_owned`) is set, or when the existing `use_fund_language`
 * default applies. It deliberately does NOT consult older language keys like
 * `language.label_equitytype` or `language.label_ownership` — those remain
 * the call site's responsibility and must be preserved as fallbacks beneath
 * the resolver. Wiring should be:
 *     const resolved = resolveOwnedLabel(clientData, classKey);
 *     if (resolved !== null) return resolved;
 *     // existing branch logic with label_equitytype / label_ownership / etc.
 *
 * Both functions are pure. They tolerate missing input (null clientData,
 * missing equity/language maps, unknown classKey) without throwing — callers
 * frequently invoke them during early render before all data is hydrated.
 */

export const DENOMINATIONS = Object.freeze(['shares', 'dollars', 'percent']);

/**
 * Resolve a class's effective denomination.
 *
 * @param {object|null|undefined} clientData
 * @param {string|null|undefined} classKey
 * @returns {'shares'|'dollars'|'percent'}
 */
export function getEffectiveDenomination(clientData, classKey) {
	const classOverride = clientData?.equity?.[classKey]?.denomination;
	if (typeof classOverride === 'string' && DENOMINATIONS.includes(classOverride)) {
		return classOverride;
	}
	return clientData?.use_fund_language === true ? 'dollars' : 'shares';
}

/**
 * Resolve the label rendered for a class's `owned` field.
 *
 * Returns `null` (not an empty string) when no override applies, so callers
 * can distinguish "no resolver answer — use legacy default" from "configured
 * to empty string".
 *
 * @param {object|null|undefined} clientData
 * @param {string|null|undefined} classKey
 * @returns {string|null}
 */
export function resolveOwnedLabel(clientData, classKey) {
	const classEntry = clientData?.equity?.[classKey];

	// (1) per-class label only when the class also overrides denomination
	if (classEntry?.denomination && typeof classEntry?.label_owned === 'string' && classEntry.label_owned.length) {
		return classEntry.label_owned;
	}

	// (2) client-wide language override
	const clientLevel = clientData?.language?.label_owned;
	if (typeof clientLevel === 'string' && clientLevel.length) {
		return clientLevel;
	}

	// (3) existing use_fund_language default
	if (clientData?.use_fund_language === true) {
		return 'Contributed';
	}

	// (4) caller's existing platform default applies
	return null;
}
